import { Link, useNavigate } from "react-router-dom";
import { Minus, Plus, X } from "lucide-react";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { useCart, FREE_SHIPPING_MIN } from "@/context/CartContext";
import { money } from "@/lib/api";

export function CartDrawer() {
  const { items, open, setOpen, setQty, subtotal } = useCart();
  const nav = useNavigate();
  const pct = Math.min(100, (subtotal / FREE_SHIPPING_MIN) * 100);

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetContent className="flex w-full flex-col border-l-0 bg-[var(--bone)] p-0 sm:max-w-md" data-testid="cart-drawer" data-lenis-prevent>
        <SheetHeader className="border-b border-[var(--line)] p-6">
          <SheetTitle className="font-display text-3xl font-normal">Your bag</SheetTitle>
          <div className="pt-3">
            <p className="text-xs text-[var(--ink-soft)]" data-testid="cart-shipping-progress-text">
              {subtotal >= FREE_SHIPPING_MIN ? "You've unlocked free shipping." : `${money(FREE_SHIPPING_MIN - subtotal)} away from free shipping`}
            </p>
            <div className="mt-2 h-1 overflow-hidden rounded-full bg-[var(--sand)]">
              <div className="h-full bg-[var(--terra)] transition-[width] duration-700" style={{ width: `${pct}%` }} />
            </div>
          </div>
        </SheetHeader>
        <div className="flex-1 overflow-y-auto px-6">
          {items.length === 0 && (
            <div className="py-20 text-center" data-testid="cart-empty">
              <p className="font-display text-2xl">Nothing here yet.</p>
              <button onClick={() => { setOpen(false); nav("/shop"); }} className="link-u mt-4 text-sm font-semibold" data-testid="cart-empty-shop-link">Browse the edit →</button>
            </div>
          )}
          {items.map((i) => (
            <div key={i.product_id} className="flex gap-4 border-b border-[var(--line)] py-5" data-testid={`cart-item-${i.slug}`}>
              <Link to={`/product/${i.slug}`} onClick={() => setOpen(false)} className="h-24 w-20 shrink-0 overflow-hidden rounded-lg bg-[var(--sand)]">
                <img src={i.image} alt={i.name} className="h-full w-full object-cover" />
              </Link>
              <div className="flex flex-1 flex-col justify-between">
                <div className="flex justify-between gap-2">
                  <p className="text-sm font-semibold leading-snug">{i.name}</p>
                  <button onClick={() => setQty(i.product_id, 0)} aria-label="Remove" data-testid={`cart-remove-${i.slug}`}><X size={16} /></button>
                </div>
                <div className="flex items-center justify-between">
                  <div className="flex items-center rounded-full border border-[var(--line)]">
                    <button className="grid h-8 w-8 place-items-center" onClick={() => setQty(i.product_id, i.quantity - 1)} data-testid={`cart-decrease-${i.slug}`}><Minus size={14} /></button>
                    <span className="w-6 text-center text-sm" data-testid={`cart-qty-${i.slug}`}>{i.quantity}</span>
                    <button className="grid h-8 w-8 place-items-center" onClick={() => setQty(i.product_id, i.quantity + 1)} data-testid={`cart-increase-${i.slug}`}><Plus size={14} /></button>
                  </div>
                  <span className="text-sm font-semibold">{money(i.price * i.quantity)}</span>
                </div>
              </div>
            </div>
          ))}
        </div>
        {items.length > 0 && (
          <div className="border-t border-[var(--line)] p-6">
            <div className="flex justify-between text-sm"><span>Subtotal</span><span className="font-semibold" data-testid="cart-subtotal">{money(subtotal)}</span></div>
            <button
              onClick={() => { setOpen(false); nav("/checkout"); }}
              className="mt-5 w-full rounded-full bg-[var(--terra)] py-4 text-sm font-semibold text-[#241a06] transition-colors hover:bg-[var(--terra-deep)]"
              data-testid="cart-drawer-checkout-button"
            >Checkout</button>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}
